<template>
  <div class="chat-input">
    <textarea
      ref="textarea"
      v-model="localValue"
      :placeholder="placeholder"
      :disabled="disabled"
      @keydown.enter.exact.prevent="handleSend"
      @keydown.esc="$emit('cancel')"
      @input="adjustHeight"
      rows="1"
    ></textarea>
    
    <button
      @click="handleSend"
      :disabled="disabled || !localValue.trim()"
      class="send-btn"
      title="Send (Enter)"
    >
      <svg v-if="!disabled" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
      </svg>
      <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clip-rule="evenodd"/>
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: 'ChatInput',

  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Type your message...',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localValue: this.value,
    };
  },

  watch: {
    value(newVal) {
      this.localValue = newVal;
    },

    localValue(newVal) {
      this.$emit('input', newVal);
    },
  },

  mounted() {
    this.adjustHeight();
  },

  methods: {
    handleSend() {
      if (this.disabled || !this.localValue.trim()) return;
      
      this.$emit('send', this.localValue.trim());
      this.localValue = '';
      this.$nextTick(() => {
        this.adjustHeight();
      });
    },

    adjustHeight() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;

      // Reset height to calculate new scroll height
      textarea.style.height = 'auto';
      
      // Set new height (max 120px)
      const newHeight = Math.min(textarea.scrollHeight, 120);
      textarea.style.height = `${newHeight}px`;
    },

    focus() {
      this.$refs.textarea?.focus();
    },
  },
};
</script>

<style scoped lang="scss">
.chat-input {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

textarea {
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 10px 14px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  font-size: 14px;
  font-family: inherit;
  color: #333;
  resize: none;
  outline: none;
  transition: all 0.2s;

  &:focus {
    background: #ffffff;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }

  .dark-mode & {
    background: #2a2a2a;
    border-color: #3a3a3a;
    color: #e0e0e0;

    &:focus {
      background: #333;
      border-color: #8b9aed;
      box-shadow: 0 0 0 3px rgba(139, 154, 237, 0.1);
    }

    &::placeholder {
      color: #666;
    }
  }
}

.send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 50%;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    transition: transform 0.2s;
  }

  &:hover:not(:disabled) svg {
    transform: translateX(2px);
  }
}
</style>
